import BaseModel from "./BaseModel";

import Logger from "../Logger";
import { removeFromArray, sortArrayByProperty } from "./utils";
import { noItx } from "../translationItx/itx";
import { isDevelopmentEnvironment } from "../estada-config";
import type GroupAddress from "./GroupAdress";

const logger = new Logger();

// //////////////////////////////////////////////////
// import ObjectFactory from "./ObjectFactory";
// ObjectFactory.registerClass("Consumer", (data?: any, parent?: BaseModel) => {
//   return new ReferenceArray(data, parent);
// });
// //////////////////////////////////////////////////

export default class ReferenceArray<T extends BaseModel> {
  public get className(): string {
    return "ReferenceArray"; // no itx
  }
  constructor(data?: Array<string>) {
    if (data) {
      this.referencedIds = data;
    }
  }

  referencedIds = new Array<string>();

  //
  // this array is for monitoring with groupAdresses are in use,
  // to allow deleting of the rest after processing
  //

  fromJSON(data: any, parent: BaseModel | undefined): void {
    if (data) {
      this.referencedIds = data;
    }
  }

  toJSON(): any {
    return this.referencedIds;
  }

  get firstItem(): T | undefined {
    if (this.referencedIds.length > 1) {
      logger.error(`ReferenceArray - firstItem: ${this.className} has more than one item ${this.referencedIds.length}`);
    }
    return state.objectRegistry.getObject(this.referencedIds[0]);
  }

  get items(): Array<T> {
    const res = new Array<T>();
    this.referencedIds.forEach((refId) => {
      const obj = state.objectRegistry.getObject(refId);
      if (obj) {
        res.push(obj as T);
      }
    });
    return res;
  }

  forEach(callbackfn: (value: T) => void): void {
    this.referencedIds.forEach((refId) => {
      const obj = state.objectRegistry.getObject(refId);
      if (obj) {
        callbackfn(obj as T);
      }
    });
  }

  // Ermöglicht direkte Iteration über BaseModelMap (z.B. for...of)
  [Symbol.iterator](): IterableIterator<T> {
    return this.asArray().values();
  }

  //
  // returns true if ObjRef has been added
  // returns false if ObjRef is already in this ReferenceArray
  //
  add(obj: T): boolean {
    for (let refId of this.referencedIds) {
      if (refId == obj.id) {
        logger.error(`avoid double add: ${refId}`);
        return false;
      }
    }

    if (isDevelopmentEnvironment()) {
      for (let refId of this.referencedIds) {
        const obj2: GroupAddress = state.objectRegistry.getObject(refId) as GroupAddress;
        if (obj.className == "GroupAddress" && obj2.className == "GroupAddress") {
          if (obj2.fullAddress == (obj as any as GroupAddress).fullAddress) {
            logger.error(`avoid double add (2): ${obj2.fullAddress}`);
            return false;
          }
        }
      }
    }

    this.referencedIds.push(obj.id);
    return true;
  }

  remove(obj: T) {
    const id = obj.id;
    this.removeId(id);
  }

  removeId(id: string) {
    removeFromArray(this.referencedIds, id);
  }

  removeIf(ifFunc: (ga: T) => boolean) {
    const toRemove = new Array<T>();
    this.referencedIds.forEach((refId) => {
      // eslint-disable-line
      const obj = state.objectRegistry.getObject(refId) as T;
      if (obj) {
        if (ifFunc(obj)) {
          toRemove.push(obj);
        }
      }
    });

    toRemove.forEach((obj) => {
      this.remove(obj);
    });
  }

  get length(): number {
    return this.referencedIds.length;
  }

  clear() {
    this.referencedIds = new Array<string>();
  }

  asArray(): Array<T> {
    return this.items;
  }

  sortBy = (propertyName: string): Array<T> => {
    return sortArrayByProperty<T>(this.asArray(), propertyName);
  };

  copy(): ReferenceArray<T> {
    return new ReferenceArray([...this.referencedIds]);
  }

  find(predicate: (value: T) => boolean): T | undefined {
    for (const refId of this.referencedIds) {
      const obj = state.objectRegistry.getObject(refId) as T;
      if (obj) {
        if (predicate(obj)) {
          return obj as T;
        }
      }
    }

    return undefined;
  }

  toString(): string {
    let res = "[";
    this.referencedIds.forEach((id) => {
      const obj = state.objectRegistry.getObject(id);
      if (obj) {
        res += `${obj?.fullTag} ${obj.name}; `;
      } else {
        res += noItx("?; ");
      }
    });

    if (res.endsWith("; ")) {
      res = res.substring(0, res.length - 2);
    }

    res += "]";

    return res;
  }

  consistencyCheck(parent: BaseModel) {
    [...this.referencedIds].forEach((id) => {
      const obj = state.objectRegistry.getObject(id);
      if (!obj) {
        parent.logConsistencyError(`ReferenceArray: ${id} not found`);
        removeFromArray(this.referencedIds, id);
      }
    });
  }
}
